import { inject, Injectable } from "@angular/core";
import { IBaseT1Card, TaxprepT1Card } from "@app/views-interfaces";
import { getCardStatusId, isCardGrouped } from "@app/views-interfaces/card-utils";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { BusinessLogicService } from "../business-logic";
import { CardDisplayService } from "../card-display";
import { DashboardsService } from "../dashboards-service";
import { IFirmUserRolesService } from "../ifirm-user-roles-service";
import { MappingOfStatusesService } from "../mapping-of-statuses-service";
import { isUnassigned, UsersService } from "../users-service";
import { UtilsService } from "../utils/utils.service";
import { CardState, ICardDragAndDropStrategy } from "./card-drag-and-drop-strategy.interface";

@Injectable({ providedIn: "root" })
export class T1DragAndDropStrategy implements ICardDragAndDropStrategy {
    private readonly businessLogicService = inject(BusinessLogicService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly iFirmUserRolesService = inject(IFirmUserRolesService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly usersService = inject(UsersService);

    captureOriginalUserId(card: IBaseT1Card): string {
        const roleId = this.mappingOfStatusesService.getStatusFromStatusId(getCardStatusId(card))?.RoleId;
        return card.roleUserMapping?.find((mapping) => mapping.roleId === roleId)?.userId ?? "";
    }

    captureOriginalStatusIds(card: IBaseT1Card): Set<string> {
        return new Set(
            this.getAffectedCards(card)
                .map((c) => getCardStatusId(c))
                .filter((element, index, array) => UtilsService.unique(element, index, array)),
        );
    }

    isValidStatusChange(card: IBaseT1Card, statusId: string): boolean {
        return card && !!statusId && (getCardStatusId(card) !== statusId || this.captureOriginalStatusIds(card).size > 1);
    }

    isValidAssigneeChange(card: IBaseT1Card, targetUserId: string, statusId?: string): boolean {
        const roleId = this.getRoleIdForStatus(statusId);
        if (!card || !roleId || !targetUserId) return false;

        const assignedUserId = card.roleUserMapping.find((mapping) => mapping.roleId === roleId)?.userId;
        if (isUnassigned(targetUserId) && !isUnassigned(assignedUserId)) {
            return true;
        }
        const targetUser = this.usersService.getUserById(targetUserId);
        const targetUserHasRole = this.iFirmUserRolesService.iFirmUserRoles.some((ur) => ur.UserId == targetUser?.IFirmUserId && ur.RoleId == roleId);
        return !isUnassigned(targetUserId) && targetUserId !== assignedUserId && !!targetUser && targetUser.Active && targetUserHasRole;
    }

    async onStatusDrop(card: IBaseT1Card, statusId: string): Promise<void> {
        const dashboard = this.dashboardsService.currentDashboard();
        const grouped = isCardGrouped(card, dashboard);
        this.businessLogicService.saveChanges(card, [{ field: "returnStatus", value: statusId }], {
            applyReturnStatusToAll: grouped,
            applyAssigneesToAll: false,
        });
    }

    onAssigneeDrop(card: IBaseT1Card, targetUserId: string, statusId?: string): void {
        const roleId = this.getRoleIdForStatus(statusId);
        if (!roleId) return;
        const dashboard = this.dashboardsService.currentDashboard();
        const grouped = isCardGrouped(card, dashboard);
        this.businessLogicService.saveChanges(card, [{ field: "roles", value: { roleId, userId: targetUserId } }], {
            applyReturnStatusToAll: false,
            applyAssigneesToAll: grouped,
        });
    }

    getFirstValidStatusOf(state: CardState): ITaxReturnStatus | null {
        return this.mappingOfStatusesService.getFirstStatusOf(state);
    }

    private getAffectedCards(card: IBaseT1Card): IBaseT1Card[] {
        let affectedCards: IBaseT1Card[] = [card];
        const dashboard = this.dashboardsService.currentDashboard();
        if (isCardGrouped(card, dashboard)) {
            affectedCards = (card.members ?? [])
                .map((member) => this.cardDisplayService.getCardById(member.cardId) as TaxprepT1Card | undefined)
                .filter((member): member is TaxprepT1Card => member != undefined && isCardGrouped(member, dashboard)) // If the members are not all in cache, we just skip them for the moment so we don't crash
                .reduce((acc, c) => {
                    acc.push(c);
                    return acc;
                }, affectedCards);
        }
        return affectedCards;
    }

    private getRoleIdForStatus(statusId: string | undefined): string | undefined {
        if (!statusId) return undefined;
        return this.mappingOfStatusesService.Steps.flatMap((step) => step.Statuses).find((mapping) => mapping.TaxReturnStatusId === statusId)?.RoleId;
    }
}
